import { motion } from 'motion/react'
import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useModal } from '@app/context/ModalsContext'

import { LinkProps } from '@ui/components/Link'
import { SegmentedControl } from '@ui/components/SegmentedControl/SegmentedControl'

import { useGAEvents } from '@utils/callGAEvents'

import { OldLegalDocumentDto } from '@model/LegalDocumentDto'
import { MultiLevelCommissionDetailDto } from '@model/MultiLevelCommission'
import { PayoutSchemeDto } from '@model/PayoutScheme'

import { Loading } from '@shared/components/Loading/Loading'

import { NewLevelBanner } from '../components/MultiLevel/NewLevelBanner'
import { SectionHeader } from '../components/SectionHeader/SectionHeader'
import { PayoutSchemeMetaData, PayoutSchemeTab } from '../hooks/usePayoutScheme'
import { FormulaForRewardsModal } from './FormulaForRewards.modal'
import { PayoutSchemeAssetClass } from './PayoutSchemeAsset'
import { ProgressBars } from './ProgressBars'

import styles from './PayoutScheme.module.scss'

interface MultiLevelProps {
  isLoading: boolean
  legalDocuments: OldLegalDocumentDto[]
  multiLevelData?: MultiLevelCommissionDetailDto
  payoutSchemeData?: PayoutSchemeDto[]
  schemeMetaDataByTabId: Map<number, PayoutSchemeMetaData>
  dismissNotification: () => Promise<void>
}

export const PayoutScheme: FC<MultiLevelProps> = (props) => {
  const {
    isLoading,
    legalDocuments,
    multiLevelData,
    payoutSchemeData,
    dismissNotification,
    schemeMetaDataByTabId,
  } = props

  const { t } = useTranslation()
  const [isBannerShown, setIsBannerShown] = useState(false)
  const [activeTab, setActiveTab] = useState<PayoutSchemeTab>()
  const { callGAEvents } = useGAEvents()

  const activePayoutScheme = useMemo(() => {
    return payoutSchemeData?.find((scheme) => scheme.assetClass.id === activeTab)
  }, [activeTab, payoutSchemeData])

  const seeMoreLinkProps = useMemo<LinkProps | undefined>(() => {
    const agreementLink = legalDocuments.find(
      (doc) => doc.code === 'sc_introducing_broker_agreement'
    )?.url

    if (!agreementLink) {
      return undefined
    }

    return {
      onClick: () => callGAEvents().IBDashboard.MultiLevelCommission.seeMore(),
      to: agreementLink,
      target: '_blank',
    }
  }, [callGAEvents, legalDocuments])

  const handleHideClick = async () => {
    setIsBannerShown(false)
    await dismissNotification()
  }

  useEffect(() => {
    setIsBannerShown(!!multiLevelData?.showLevelNotification)
  }, [multiLevelData])

  const infoModal = useModal(FormulaForRewardsModal)

  const tabIds = useMemo(
    () => [...schemeMetaDataByTabId.keys()].sort((a, b) => a - b),
    [schemeMetaDataByTabId]
  )

  const hasTab = useMemo(() => {
    const hasTabId = (id: PayoutSchemeTab) =>
      schemeMetaDataByTabId.has(id) && !schemeMetaDataByTabId.get(id)?.hasOnlyExcludedAccountTypes

    return {
      [PayoutSchemeTab.FxMetals]: hasTabId(PayoutSchemeTab.FxMetals),
      [PayoutSchemeTab.Crypto]: hasTabId(PayoutSchemeTab.Crypto),
      [PayoutSchemeTab.Other]: hasTabId(PayoutSchemeTab.Other),
    }
  }, [schemeMetaDataByTabId])

  useEffect(() => {
    if (tabIds.length > 0 && activeTab === undefined) {
      const defaultTab = Object.entries(hasTab).find(([, show]) => show)
      setActiveTab(Number(defaultTab?.[0]) || undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabIds, hasTab])

  if (tabIds.length === 0 && !isLoading) {
    return null
  }

  const tabs = [
    {
      id: PayoutSchemeTab.FxMetals,
      name: t('Instruments.FX / Metals'),
      show: hasTab[PayoutSchemeTab.FxMetals],
    },
    {
      id: PayoutSchemeTab.Crypto,
      name: t('Instruments.Crypto'),
      show: hasTab[PayoutSchemeTab.Crypto],
    },
    {
      id: PayoutSchemeTab.Other,
      name: t('Instruments.Other'),
      show: hasTab[PayoutSchemeTab.Other],
    },
  ]
    .filter(({ show }) => show)
    .map(({ id, name }) => ({
      id,
      name,
      onClick: () => setActiveTab(id),
    }))

  const motionContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.05,
      },
    },
  }

  const motionItem = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: [0.3, 0.1, 0.3, 1] as const,
      },
    },
  }

  return (
    <div className={styles.payoutScheme}>
      <SectionHeader
        title={t('IB.Dashboard.Your Payout Scheme.Header')}
        seeMoreLinkProps={seeMoreLinkProps}
      />
      <Loading isLoading={isLoading} showLoadingIcon wrapperClass={styles.loadingWrapper}>
        <motion.div variants={motionContainer} initial='hidden' animate='show' key={activeTab}>
          <div className={styles.tabs}>
            <SegmentedControl activeTab={activeTab} tabs={tabs} />
          </div>

          {isBannerShown && multiLevelData && activeTab === PayoutSchemeTab.FxMetals && (
            <motion.div variants={motionItem}>
              <NewLevelBanner multiLevelData={multiLevelData} onHideClick={handleHideClick} />
            </motion.div>
          )}

          <motion.div variants={motionItem}>
            <PayoutSchemeAssetClass
              activePayoutScheme={activePayoutScheme}
              onInfoModalOpen={infoModal.open}
            />
          </motion.div>

          {activePayoutScheme?.kpi && (
            <motion.div variants={motionItem}>
              <ProgressBars multiLevelKpiActivity={activePayoutScheme.kpi} />
            </motion.div>
          )}
        </motion.div>
      </Loading>
    </div>
  )
}
